<template>
  <section class="ground-station-plane-selector" :class="{ popup }" :aria-label="$t('飞机列表')">
    <div class="panel-heading gs-section-heading">
      <span>{{ $t("机队") }}</span>
      <GroundStationStatusBadge tone="muted">{{ $t("{0} 架 · {1} 在线", [(planes.length), (onlineCount)]) }}</GroundStationStatusBadge>
    </div>
    <UiMotionList class="fleet-list">
      <button v-for="plane in planes" :key="plane.id" type="button" class="fleet-plane" :class="{ 'is-selected': String(plane.id) === String(selectedPlaneId) }" :aria-pressed="String(plane.id) === String(selectedPlaneId)" :aria-label="`${plane.name || `飞机 ${plane.id}`}，${isOnline(plane) ? '在线' : '离线'}，${isDelivering(plane) ? '配送中' : '待命'}`" @click="$emit('select', String(plane.id))">
        <GroundStationHeartbeatBackdrop v-if="isOnline(plane) || (plane.planeState && plane.planeState.espOnline)" :online="isOnline(plane)" :esp-online="Boolean(plane.planeState && plane.planeState.espOnline)" :heartbeat-tone="heartbeatTone(plane)" :pulse-key="plane.planeState && plane.planeState.espRandom" compact />
        <div class="fleet-plane__title"><i class="iconfont icon-wurenji" :style="planeSymbolStyle(plane)" aria-hidden="true"></i><strong :title="plane.name || `飞机 ${plane.id}`">{{ plane.name || `飞机 ${plane.id}` }}</strong><span :class="isOnline(plane) ? 'is-online' : 'is-offline'">{{ $t(isOnline(plane) ? '在线' : '离线') }}</span></div>
        <div class="fleet-plane__states"><UiTag v-for="item in stateTags(plane)" :key="item.key" :tone="item.tone" size="mini" :title="item.text"><span class="ui-tag__text">{{ $t(item.text) }}</span></UiTag><span :class="{ 'is-delivering': isDelivering(plane) }">{{ $t(isDelivering(plane) ? '配送中' : '待命') }}</span></div>
        <i class="fleet-plane__battery"><em class="remaining-power" :style="batteryProgressStyle(plane, false)"></em><em v-if="hasRtlBattery(plane)" class="rtl-power" :style="batteryProgressStyle(plane, true)"></em></i>
        <div v-if="hasCargoModule(plane)" class="fleet-plane__payload" :class="{ 'is-fault': hasTopFault(plane) }" :title="$t(payloadStatusText(plane))">{{ $t("挂钩 {0}", [$t(payloadStatusText(plane))]) }}</div>
        <div v-if="hasParabolaModule(plane)" class="fleet-plane__payload">{{ $t("抛物器 {0}", [$t(parabolaStatusText(plane))]) }}</div>
      </button>
    </UiMotionList>
    <div v-if="!planes.length" class="empty-state">{{ $t("暂无可用飞机") }}</div>
  </section>
</template>

<script>
import geodist from 'geodist'
import GroundStationHeartbeatBackdrop from '@/components/ground-station/GroundStationHeartbeatBackdrop'
import GroundStationStatusBadge from '@/components/ground-station/GroundStationStatusBadge'
import { getHeartbeatHealth } from '@/utils/heartbeatHealth'
import { hasPlaneModule } from '@/utils/planeModules'
import { getHookStatusLabel, isHookTopFault } from '@/utils/hookStatus'
import { getFlightModeLabel, hasValidGpsFix } from '@/utils/flightStatus'

export default {
  name: 'GroundStationPlaneSelector',
  components: { GroundStationHeartbeatBackdrop, GroundStationStatusBadge },
  props: {
    planes: { type: Array, default: () => [] },
    selectedPlaneId: { type: [String, Number], default: '' },
    deliveringPlaneIds: { type: Array, default: () => [] },
    popup: { type: Boolean, default: false }
  },
  data () { return { heartbeatHealthNow: Date.now() } },
  computed: {
    onlineCount () { return this.planes.filter(this.isOnline).length },
    batteryMetrics () {
      const metrics = new Map()
      this.planes.forEach(plane => {
        metrics.set(plane, {
          remaining: this.calculateBatteryPercent(plane),
          rtl: this.calculateRtlBatteryPercent(plane)
        })
      })
      return metrics
    }
  },
  mounted () {
    this.heartbeatHealthTimer = setInterval(() => { this.heartbeatHealthNow = Date.now() }, 1000)
  },
  beforeDestroy () {
    if (this.heartbeatHealthTimer) clearInterval(this.heartbeatHealthTimer)
  },
  methods: {
    hasCargoModule (plane) { return hasPlaneModule(plane, 'cable_transport') },
    hasParabolaModule (plane) { return hasPlaneModule(plane, 'parabola') },
    parabolaStatusText (plane) {
      const state = plane?.planeState
      if (!state?.espOnline || typeof state.parabolaServo !== 'boolean') return '状态未知'
      return state.parabolaServo ? '开启' : '关闭'
    },
    planeSymbolStyle (plane) {
      const color = String(plane?.color || '').trim()
      if (!/^#[0-9a-fA-F]{6}$/.test(color)) return null
      return { color }
    },
    isOnline (plane) { return Boolean(plane?.planeState?.online) },
    hasValidFix (fixType) { return hasValidGpsFix(fixType) },
    heartbeatTone (plane) {
      const state = plane?.planeState || {}
      if (!this.isOnline(plane) || !state.heartbeatObservedAt || !Array.isArray(state.heartbeatSamples) || !state.heartbeatSamples.length) return 'muted'
      return getHeartbeatHealth(state, Math.max(this.heartbeatHealthNow, Number(state.heartbeatHealthUpdatedAt) || 0)).level
    },
    isDelivering (plane) { return this.deliveringPlaneIds.some(planeId => String(planeId) === String(plane.id)) },
    hasTopFault (plane) { return isHookTopFault(plane?.planeState?.hookstatus) },
    payloadStatusText (plane) {
      const state = plane?.planeState || {}
      const hookStatus = getHookStatusLabel(state.hookstatus)
      const rawLoadWeight = state.loadweight
      const loadWeight = rawLoadWeight === null || rawLoadWeight === undefined || rawLoadWeight === '' ? NaN : Number(rawLoadWeight)
      const weightText = Number.isFinite(loadWeight) ? `${Math.round(loadWeight)}克` : '--克'
      return `${hookStatus}\u00a0${weightText}`
    },
    calculateBatteryPercent (plane) {
      const value = Number(plane?.planeState?.batteryRemaining)
      return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0
    },
    calculateRtlBatteryPercent (plane) {
      const state = plane?.planeState
      const position = Array.isArray(state?.position) && state.position.length ? state.position[state.position.length - 1] : null
      const home = state?.homePosition
      const speed = Number(state?.wpnavSpeed)
      const capacity = Number(state?.battCapacity)
      const current = Math.abs(Number(state?.currentBattery))
      if (!position || !home || !Number.isFinite(speed) || speed <= 0 || !Number.isFinite(capacity) || capacity <= 0 || !Number.isFinite(current) || current <= 0) return null
      const horizontalDistance = geodist({ lat: position[1], lon: position[0] }, { lat: home.lat, lon: home.lon }, { unit: 'meters' })
      const verticalDistance = Math.abs(Number(position[2]) - Number(home.alt))
      const rtlTime = Math.floor((horizontalDistance + verticalDistance) / speed)
      return Math.max(0, Math.min(100, rtlTime * current / 3.6 / capacity * 100))
    },
    getBatteryMetrics (plane) {
      return this.batteryMetrics.get(plane) || { remaining: 0, rtl: null }
    },
    hasRtlBattery (plane) { return this.getBatteryMetrics(plane).rtl !== null },
    batteryProgressStyle (plane, rtl) {
      const { remaining, rtl: rtlValue } = this.getBatteryMetrics(plane)
      const value = rtl ? rtlValue : remaining
      const comparedValue = rtl ? remaining : rtlValue
      return { width: `${value || 0}%`, zIndex: comparedValue === null || value <= comparedValue ? 2 : 1 }
    },
    stateTags (plane) {
      const state = plane?.planeState || {}
      const online = this.isOnline(plane)
      const fixed = online && this.hasValidFix(state.fixType)
      const tags = [
        { key: 'mode', text: getFlightModeLabel(state.getPlaneMode) || '未知模式', tone: this.isOnline(plane) ? 'warning' : 'neutral' },
        { key: 'fix', text: fixed ? '已定位' : '未定位', tone: !online ? 'neutral' : (fixed ? 'primary' : 'danger') }
      ]
      return tags
    }
  }
}
</script>

<style lang="scss" scoped>
.ground-station-plane-selector { width: 100%; min-width: 0; box-sizing: border-box; }
.panel-heading { min-height: 44px; display: flex; align-items: center; justify-content: space-between; padding: 0 var(--ui-space-sm); }
.fleet-list { display: grid; gap: var(--ui-space-sm); padding: 0 var(--ui-space-sm) var(--ui-space-sm); }
.fleet-plane { min-width: 0; position: relative; overflow: hidden; padding: var(--ui-space-sm); color: var(--ui-text-primary); background: var(--ui-surface-finish), var(--ui-bg-elevated); border: 1px solid var(--ui-border-color); border-radius: var(--ui-radius-large); text-align: left; cursor: pointer; font: inherit; }
.fleet-plane > :not(.ground-station-heartbeat-backdrop) { position: relative; z-index: 1; }
.fleet-plane.is-selected { border-color: var(--ui-action-primary); }
.fleet-plane:focus-visible { outline: 2px solid var(--ui-action-primary); outline-offset: 2px; }
@media (hover: hover) and (pointer: fine) { .fleet-plane:hover { background-color: var(--ui-bg-panel); border-color: var(--ui-text-secondary); } }
.fleet-plane__title { display: flex; gap: var(--ui-space-xs); align-items: center; }
.fleet-plane__title strong { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--ui-font-size-body); }
.fleet-plane__title > span, .fleet-plane__states, .fleet-plane__payload { font-size: var(--ui-font-size-caption); }
.is-online { color: var(--ui-color-primary); }
.is-offline { color: var(--ui-text-secondary); }
.is-delivering { color: var(--ui-color-warning); }
.fleet-plane__states { display: flex; align-items: center; gap: var(--ui-space-xs); margin-top: var(--ui-space-xs); color: var(--ui-text-secondary); }
.fleet-plane__states .ui-tag { min-width: 0; }
.fleet-plane__states > span:last-child { margin-left: auto; white-space: nowrap; }
.fleet-plane__battery { display: block; height: 3px; margin-top: var(--ui-space-xs); position: relative; overflow: hidden; background: var(--ui-border-color); border-radius: var(--ui-radius-small); }
.fleet-plane__battery em { position: absolute; left: 0; top: 0; height: 100%; }
.remaining-power { background: var(--ui-color-primary); }
.rtl-power { background: var(--ui-color-warning); }
.fleet-plane__payload { margin-top: var(--ui-space-xs); color: var(--ui-text-secondary); overflow-wrap: anywhere; }
.fleet-plane__payload.is-fault { color: var(--ui-color-danger); }
.empty-state { padding: var(--ui-space-lg); text-align: center; color: var(--ui-text-secondary); }
</style>
